import React, { useEffect, useState } from 'react';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import {
  Box,
  Typography,
} from '@mui/material';

import { getTreeForAccess, getEligibleViewers, assignUsersToForm } from '../controllers/form.controller';

const FormAccessManagerPage = () => {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 14); // چون امروز هم شامل می‌شود
  const [treeData, setTreeData] = useState([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [startDate, setStartDate] = useState(new DateObject({ date: sevenDaysAgo, calendar: persian, locale: persian_fa }));
  const [endDate, setEndDate] = useState(new DateObject({ date: today, calendar: persian, locale: persian_fa }));
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedForm, setSelectedForm] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [checked, setChecked] = useState([]);


  // فرمت تاریخ شمسی
  const formatFaDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return new Intl.DateTimeFormat('fa-IR').format(d);
  };

  const toEnglishDigits = (str = '') => str.replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));

  const convertPersianToISO = (persianDate) => {
    if (!persianDate) return '';

    const d = new DateObject({
      date: persianDate,
      format: 'YYYY/MM/DD',
      calendar: persian,
    });

    return d.toDate().toISOString();
  };

  useEffect(() => {

    const startISO = startDate ? startDate.toDate().toISOString() : '';

    let endISO = '';
    if (endDate) {
      const d = endDate.toDate();
      d.setHours(23, 59, 59, 999);
      endISO = d.toISOString();
    }
    fetchTreeData({
      search: search,
      type: type,
      startDate: startISO,
      endDate: endISO
    });
  }, [search, type, startDate, endDate, page]);

  const fetchTreeData = async ({ search, type, page, limit, startDate, endDate }) => {
    setLoading(true);
    try {
      const res = await getTreeForAccess({
        search,
        type,
        page,
        limit,
        startDate,  
        endDate,    
      });

      const data = res.data;
      const tree = [];

      const projectMap = {};
      const clinicMap = {};
      const pageMap = {};

      data.forEach((item) => {
        const projectId = item.projectId;
        const clinicId = item.clinicId;
        const pageId = item.pageId;
        const formId = item.formId;

        if (!projectMap[projectId]) {
          const node = {
            key: `project-${projectId}`,
            data: {
              title: item.projectTitle?.fa || 'بدون عنوان',
              projectType: item.projectType || '—',
              type: 'Project',
            },
            children: [],
          };
          projectMap[projectId] = node;
          tree.push(node);
        }

        if (!clinicMap[clinicId]) {
          const node = {
            key: `clinic-${clinicId}`,
            data: {
              title: item.clinicTitle?.fa || 'بدون عنوان',
              type: 'Clinic',
            },
            children: [],
          };
          clinicMap[clinicId] = node;
          projectMap[projectId].children.push(node);
        }

        if (!pageMap[pageId]) {
          const node = {
            key: `page-${pageId}`,
            data: {
              title: item.pageTitle?.fa || 'بدون عنوان',
              type: 'Page',
            },
            children: [],
          };
          pageMap[pageId] = node;
          clinicMap[clinicId].children.push(node);
        }

        const formNode = {
          key: `form-${formId}`,
          data: {
            title: item.formTitle?.fa || 'بدون عنوان',
            type: 'Form',
          },
          leaf: true,
        };
        pageMap[pageId].children.push(formNode);
      });

      setTreeData(tree);
      setTotalCount(res.totalCount || 0);
    } catch (error) {
      console.error('خطا در دریافت ساختار درختی:', error);
    } finally {
      setLoading(false);
    }
  };



  const openUserDialog = async (formRow) => {
    setSelectedForm(formRow);
    try {
      const res = await getEligibleViewers(formRow.key.replace('form-', ''));
      const all = res.data || [];
      setUsers(all);
      setChecked(all.filter((u) => u.isAssigned).map((u) => u._id));
      setDialogOpen(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleUser = (userId) => {
    setChecked((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSaveUsers = async () => {
    try {
      if (!selectedForm) return;
      await assignUsersToForm(selectedForm.key.replace('form-', ''), checked);
      setDialogOpen(false);
    } catch (err) {
      console.error(err.message);
    }
  };

  const actionTemplate = (node) => {
    if (node.data.type === 'Form') {
      return (
        <Button
          label="کاربران فرم"
          icon="pi pi-users"
          className="p-button-sm p-button-info"
          onClick={() => openUserDialog(node)}
        />
      );
    }
    return null;
  };

  const exportToPDF = () => {
    try {
      const flatten = (nodes) => nodes.flatMap(n => [
        [n.data.title, n.data.type],
        ...flatten(n.children || [])
      ]);
      const doc = new jsPDF();
      doc.autoTable({
        head: [['عنوان', 'نوع']],
        body: flatten(treeData)
      });
      doc.save('form-access.pdf');
    }
    catch (err) {
      toast.error(err.message);
    }
  };

  const exportToExcel = () => {
    try {
      const flatten = (nodes) => nodes.flatMap(n => [
        { ...n.data },
        ...flatten(n.children || [])
      ]);
      const worksheet = XLSX.utils.json_to_sheet(flatten(treeData));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Forms');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(data, 'form-access.xlsx');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="p-4" dir="rtl">
      <ToastContainer position="top-right" autoClose={3000} />
      <h2 className="p-text-bold p-mb-3">مدیریت دسترسی کاربران به فرم‌ها</h2>

      <div className="p-card p-shadow-3 p-p-3 p-mb-4"
        style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span className="p-input-icon-left" style={{ flex: '1 1 200px' }}>
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو در عنوان پروژه"
            style={{ width: '100%' }}
          />
        </span>

        <Dropdown
          value={type}
          onChange={(e) => setType(e.value)}
          options={[
            { label: 'همه', value: '' },
            { label: 'آزمایشی', value: 'trial' },
            { label: 'اصلی', value: 'main' }
          ]}
          placeholder="نوع پروژه"
          style={{ flex: '1 1 150px' }}
        />



        <Box
          sx={{
            display: 'flex',
            gap: 4,                 // فاصله واقعی
            alignItems: 'flex-end',
          }}
        >
          {/* از تاریخ */}
          <Box sx={{ minWidth: 170 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5, textAlign: 'left', fontSize: 16, fontWeight: 500, color: 'text.secondary' }}
            >
              از تاریخ
            </Typography>

            <Box sx={{ width: 170, display: 'block' }}>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                value={startDate}
                onChange={(date) => setStartDate(date || null)}
                style={{ width: '100%', height: 40 }}
              />
            </Box>
          </Box>

          {/* تا تاریخ */}
          <Box sx={{ minWidth: 170 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, textAlign: 'left', fontSize: 16, fontWeight: 500, color: 'text.secondary' }}
            >
              تا تاریخ
            </Typography>

            <Box sx={{ width: 170, display: 'block' }}>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                value={endDate}
                onChange={(date) => setEndDate(date || null)}
                style={{ width: '100%', height: 40 }}
              />
            </Box>
          </Box>
        </Box>

        <Button
          label="اعمال فیلتر"
          icon="pi pi-filter"
          className="p-button-primary p-button-raised"
          style={{ flex: '1 1 120px', whiteSpace: 'nowrap' }}
          onClick={fetchTreeData}
        />
      </div>

      <div
        className="p-card p-shadow-3 p-p-3 p-mb-4"
        style={{
          marginTop: '1.5rem',   // 👈 این خط فاصله بین فیلتر و کارت
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}
      ></div>

      <div className="p-d-flex p-ai-center p-mb-3 gap-2">
        <Button label="خروجی PDF" icon="pi pi-file-pdf" className="p-button-danger" onClick={exportToPDF} />
        <Button label="خروجی Excel" icon="pi pi-file-excel" className="p-button-success" onClick={exportToExcel} />
      </div>

      {loading ? (
        <div className="p-text-center p-mt-5">
          <ProgressSpinner />
        </div>
      ) : (
        <TreeTable value={treeData} className="ltr-table" tableStyle={{ minWidth: '100%' }}>
          <Column field="title" header="عنوان" expander />
          <Column field="type" header="نوع" />
          <Column field="projectType" header="نوع پروژه" />
          <Column body={actionTemplate} header="عملیات" style={{ width: '180px' }} />
        </TreeTable>
      )}

      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header="تخصیص کاربران Viewer"
        breakpoints={{ '960px': '95vw' }}
        style={{ width: '35vw' }}
      >
        {users.map(user => (
          <div key={user._id} className="p-field-checkbox p-mb-2">
            <Checkbox
              inputId={user._id}
              checked={checked.includes(user._id)}
              onChange={() => handleToggleUser(user._id)}
            />
            <label htmlFor={user._id} className="p-ml-2">
              {user.fullName || user.email || user.phone}
            </label>
          </div>
        ))}

        <div className="p-d-flex p-jc-between p-mt-3">
          <Button label="انصراف" icon="pi pi-times" className="p-button-secondary" onClick={() => setDialogOpen(false)} />
          <Button label="ذخیره" icon="pi pi-check" onClick={handleSaveUsers} />
        </div>
      </Dialog>

      {totalCount > limit && (
        <div className="p-d-flex p-jc-center p-mt-4 p-ai-center p-gap-3">
          <Button label="صفحه قبل" icon="pi pi-angle-right" disabled={page === 1} onClick={() => setPage(page - 1)} />
          <span>صفحه {page} از {Math.ceil(totalCount / limit)}</span>
          <Button label="صفحه بعد" icon="pi pi-angle-left" disabled={page >= Math.ceil(totalCount / limit)} onClick={() => setPage(page + 1)} />
        </div>
      )}
    </div>
  );
};

export default FormAccessManagerPage;
